import { lazy, ReactNode, Suspense, useContext, useEffect } from "react";
import { Navigate, RouteObject, useLocation, useNavigate, useRoutes } from "react-router-dom";
import { useTheme } from "@mui/material/styles";
import LogRocket from "logrocket";
import setupLogRocketReact from "logrocket-react";

import CalendarPage from "../calendar/Calendar";
import Checks from "../checks/Checks";
import DeadChecks from "../checks/deadChecks/DeadChecks";
import { ChecksContextProvider } from "../checks/ChecksContext";
import ClosingsPage from "../closings/ClosingsPage";
import CommandPalette from "../components/CommandPalette";
import DashboardLayout from "../components/layouts/dashboard";
import PageConfirmation from "../confirmationForms/PageConfirmation";
import DepositRefundsPage from "../depositRefunds/DepositRefundsPage";
import NewDepositRefundRequest from "../depositRefunds/NewDepositRefundRequest";
import OrderDocumentsPage from "../documents/OrderDocumentsPage";
import OrderNumberPrompt from "../orderEntry/OrderNumberPrompt";
import PageOrderEntry from "../orderEntry/PageCreateOrder";
import PageNotes from "../orderEntry/PageNotes";
import AddEditCompanyForm from "../phonebook/company/addEditCompany/AddEditCompany";
import CompaniesPage from "../phonebook/company/CompaniesPage";
import { CompanyProvider } from "../phonebook/company/CompanyContext";
import AddEditContactForm from "../phonebook/contact/addEditContact/AddEditContactForm";
import ContactsPage from "../phonebook/contact/ContactsPage";
import ContactList from "../phonebook/contactsList/ContactList";
import ManageTasksPage from "../recordables/manageTasks/ManageTasksPage";
import RecordablesPage from "../recordables/RecordablesPage";
import TasksPage from "../recordables/TasksPage";
import CommitmentsOverdue from "../reports/commitmentsOverdue/CommitmentsOverdue";
import { CommitmentsOverdueContextProvider } from "../reports/commitmentsOverdue/CommitmentsOverdueContext";
import Dashboard from "../reports/dashboard/Dashboard";
import OutstandingSearches from "../reports/outstandingSearches/OutstandingSearches";
import { OutstandingSearchesContextProvider } from "../reports/outstandingSearches/OutstandingSearchesContext";
import PageSearchOrder from "../searches/PageOrderSearches";
import { logPageView } from "../utils/analyticsLogger";

import CorpDocsPage from "../corpdocs/CorpDocsPage";
import CorpDocsManageTasksPage from "../corpdocs/manageTasks/ManageTasksPage";
import CorpDocsTasksPage from "../corpdocs/TasksPage";

import EditContactHistory from "../admin/ContactHistory/EditContactHistory";
import SuspectedMatches from "../admin/ContactHistory/SuspectedMatches";
import RedFlags from "../admin/RedFlags/RedFlags";
import AddBlacklistName from "../Blacklist/AddBlacklistName";
import AddBlacklistProperty from "../Blacklist/AddBlacklistProperty";
import EditBlacklistName from "../Blacklist/EditBlacklistName";
import EditBlacklistProperty from "../Blacklist/EditBlacklistProperty";
import BlacklistMigrationReview from "../Blacklist/MigrationReview/BlacklistMigrationReview";
import ViewBulletins from "../Blacklist/ViewBlacklist/ViewBulletins";
import CommissionTool from "../commissionTool";
import BlackListNameSearch from "../components/BlacklistNameSearch";
import NewBlackListNameSearch from "../components/BlacklistNameSearch/NewBlackListNameSearch";
import PatriotNameSearch from "../components/BlacklistNameSearch/PatriotNameSearch";
import TransactionLedger from "../components/transactionLedger";
import { FlipsDashboardContextProvider } from "../contexts/FlipsDashboardContext";
import { UserContext } from "../contexts/UserContext";
import Directory from "../directory";
import ExecutiveReports from "../executiveReports/ExecutiveReports";
import InsuranceAudit from "../insuranceAudit";
import InternalUserRoles from "../internalUserRoles";
import EditInternalUserRole from "../internalUserRoles/EditInternalUserRole";
import ClientOrders from "../orderEntry/clientOrders/ClientOrders";
import TMEOrders from "../orders/TMEOrders";
import Page404 from "../Page404";
import PageLoginSucess from "../PageLoginSucess";
import AddCompanyPage from "../phonebook/company/AddCompanyPage";
import MergeContacts from "../phonebook/contact/merge/MergeContacts";
import MergeCompaniesPage from "../phonebook/mergeCompanies/MergeCompaniesPage";
import ManagersDashboard from "../reports/dashboard/ManagersDashboard";
import MyDashboard from "../myDashboard";
import { PoliciesToSendProvider } from "../reports/dashboard/policiestosenddashbord/PoliciesToSendContext";
import PoliciesToSendDashboard from "../reports/dashboard/policiestosenddashbord/PoliciesToSendDashboard";
import EscrowLedgers from "../reports/escrowLedgers/EscrowLedgers";
import { EscrowLedgersContextProvider } from "../reports/escrowLedgers/EscrowLedgersContext";
import EscrowOverdueOrders from "../reports/escrowOverdueOrders-old/EscrowOverdueOrders";
import { EscrowOverdueOrdersContextProvider } from "../reports/escrowOverdueOrders-old/EscrowOverdueOrdersContext";
import NonZeroLedgerBalance_NY from "../reports/nonZeroLedgerBalance_NY";
import PoliciesNotIssued from "../reports/policiesNotIssued/PoliciesNotIssued";
import { PoliciesNotIssuedContextProvider } from "../reports/policiesNotIssued/PoliciesNotIssuedContext";
import ReportingPage from "../reporting/ReportingPage";
import RecordingLedgers from "../reports/recordingLedgers/RecordingLedgers";
import { RecordingLedgersContextProvider } from "../reports/recordingLedgers/RecordingLedgersContext";
import { OrderInfoProvider } from "../shared/orderInfo/OrderInfoContext";
import FlipsDashboard from "../taskManagement/flipsDashboard";
import MarkedUpCommitment from "../taskManagement/markedUpCommitment";
import Tasks from "../taskManagement/Tasks";
import Unauthorized from "../unauthorized/Unauthorized";

import ClientInteractions from "../executiveReports/ClientInteractions";
import PolicyIssuesDashboard from "../reports/dashboard/policyIssues/PolicyIssues";
import { PolicyIssuesProvider } from "../reports/dashboard/policyIssues/PolicyIssuesContext";
import NonZeroBalanceLedgers from "../reports/nonZeroBalanceLedgers/NonZeroBalanceLedgers";
import { NonZeroBalanceLedgersContextProvider } from "../reports/nonZeroBalanceLedgers/NonZeroBalanceLedgersContext";
import NonZeroLedgerBalanceRedirect from "../reports/nonZeroLedgerBalanceRedirect";
import SettlementsDashboard from "../settlementsDashboard";
import Seating from "../seating/index";
import PendingQaOrders from "../taskManagement/qa/PendingQaOrders";
import NewTasks from "../taskManagement/new-tasks/NewTasks";
import { AppRouteObject } from "./types";
import CustomDocumentExtractor from "../AIDocumentExtractors/CustomDocumentExtractor";
import PurchaseAgreementExtractor from "../AIDocumentExtractors/PurchaseAgreementExtractor";
import NonZeroLedgerBalanceOld from "../reports/nonZeroLedgerBalance_OLD";
import ViewContacts from "../reports/newlyAddedContacts/ViewContacts";
import DashboardPermissionsTable from "../dashboardReportPermissions/DashboardPermissionsTable";
import MultiOrderContactEditor from "../multiOrderContactEditor";
import SearchesNotOrdered from "../searchesNotOrdered/SearchesNotOrdered";
import { SearchesNotOrderedProvider } from "../searchesNotOrdered/SearchesNotOrderedContext";
import SurveyZoning from "../surveyZoning/SurveyZoning";
import { FundingAuthorizationProvider } from "../FundingAuthorization/FundingAuthorizationContext";
import FundingAuthorization from "../FundingAuthorization/EditFunding/FundingAuthorization";
import NewFundingPackageDetails from "../FundingAuthorization/EditFunding/NewFundingPackageDetails";
import BusinessInsights from "../executiveReports/businessInsights/BusinessInsights";
import MultiSiteOrderInfo from "../multiSiteOrderInfo";
import ReviewFundingPackage from "../FundingAuthorization/ReviewFunding/ReviewFundingPackage";
import ReviewFunding from "../FundingAuthorization/ReviewFunding/ReviewFunding";
import { FundingPackagesRoute } from "../FundingAuthorization";
import DailyOrdersClientInteractions from "../executiveReports/dailyOrdersClientInteractions/DailyOrdersClientInteractions";
import TrustMoneyPieChart from "../reports/trustMoneyPieChart/TrustMoneyPieChart";
import ClientCultivatorDashboard from "../clientCultivatorDashboard/ClientCultivatorDashboard";

const RecordableRejections = lazy(() => import("../reports/recordableRejections/RecordableRejections"));

const ManagerProtectedRoute = ({ element }: { element: ReactNode }) => {
  const { user } = useContext(UserContext);
  const isManager = user?.isManager;
  const navigate = useNavigate();

  useEffect(() => {
    if (user != null && !isManager) {
      navigate("/unauthorized");
    }
  }, [user, isManager]);

  return <>{isManager ? element : null}</>;
};

/**
 * The site root lands on Orders, which is a wide table with no mobile treatment.
 * Phones go to Documents instead — the search page they actually use.
 *
 * matchMedia is read synchronously rather than via useMediaQuery: this renders once
 * and immediately navigates, so a first-render `false` would redirect to Orders and
 * never re-evaluate.
 */
function RootRedirect() {
  const theme = useTheme();
  const isMobile =
    typeof window !== "undefined" &&
    window.matchMedia(theme.breakpoints.down("md").replace("@media ", "")).matches;

  return <Navigate to={isMobile ? "/documents" : "/orders"} replace />;
}

export const routes: AppRouteObject[] = [
  {
    path: "/dashboard/settlements",
    element: <SettlementsDashboard />
  },
  {
    path: "/dashboard/seating",
    element: <Seating />
  },
  { path: "/business-insights", element: <BusinessInsights /> },
  {
    path: "/",
    element: <DashboardLayout />,
    children: [
      {
        element: <RootRedirect />,
        index: true
      },
      {
        path: "directory",
        showInDirectory: false,
        children: [
          {
            element: <Directory />,
            index: true
          }
        ]
      },
      {
        path: "orders",
        children: [
          {
            element: <TMEOrders />,
            index: true
          }
        ]
      },
      {
        path: "documents",
        element: <OrderDocumentsPage />
      },
      {
        path: "documents/:orderNumber",
        element: <OrderDocumentsPage />
      },
      {
        path: "order-entry",
        children: [
          {
            element: <PageOrderEntry />,
            index: true
          }
        ]
      },
      {
        path: "order-entry/order-information",
        children: [
          {
            element: <PageOrderEntry />,
            index: true
          }
        ]
      },
      {
        path: "searches",
        children: [
          {
            element: <OrderNumberPrompt section="searches" />,
            index: true
          }
        ]
      },
      {
        path: "searches",
        children: [
          {
            path: ":orderNumber",
            element: <PageSearchOrder />,
            index: true
          }
        ]
      },
      {
        path: "confirmation",
        children: [
          {
            element: <OrderNumberPrompt section="confirmation" />,
            index: true
          }
        ]
      },
      {
        path: "notes",
        children: [
          {
            element: <OrderNumberPrompt section="notes" />,
            index: true
          }
        ]
      },
      {
        path: "confirmation",
        children: [
          {
            path: ":orderNumber",
            element: <PageConfirmation />,
            index: true
          }
        ]
      },
      {
        path: "notes",
        children: [
          {
            path: ":orderNumber",
            element: <PageNotes />,
            index: true
          }
        ]
      },
      {
        path: "contacts",
        children: [
          {
            element: <Navigate to="/phonebook/contacts/individuals" replace />,
            index: true
          }
        ]
      },
      {
        path: "calendar",
        children: [{ index: true, element: <CalendarPage /> }]
      },
      {
        path: "closings",
        children: [{ index: true, element: <ClosingsPage /> }]
      },
      {
        path: "/phonebook/contact",
        element: <Navigate to="/phonebook/contacts/profile" replace />
      },
      {
        path: "/phonebook/contact/:section",
        element: <ContactsPage />
      },
      {
        path: "/phonebook/contactsMerge",
        element: <MergeContacts />
      },
      {
        path: "/phonebook/companiesMerge",
        element: <MergeCompaniesPage />
      },
      {
        path: "/phonebook/company",
        element: <Navigate to="/phonebook/companies/profile" replace />
      },
      {
        path: "/phonebook/company/create-company",
        element: <AddCompanyPage />
      },

      {
        path: "/phonebook/company/:section",
        element: <CompaniesPage />
      },
      {
        path: "/phonebook/create-contact",
        element: <AddEditContactForm />
      },
      {
        path: "/phonebook/edit-contact",
        element: <AddEditContactForm />
      },
      {
        path: "/phonebook/edit-company",
        element: (
          <CompanyProvider>
            <AddEditCompanyForm />
          </CompanyProvider>
        )
      },
      {
        path: "/phonebook/new-contacts",
        element: <Navigate to="/phonebook/new-contacts/individuals" replace />
      },
      {
        path: "/phonebook",
        element: <Navigate to="/phonebook/contacts/individuals" replace />
      },
      {
        path: "/phonebook/contacts/:section",
        element: <ContactList />
      },
      {
        path: "/checks/dead",
        element: <DeadChecks />
      },
      {
        path: "/checks",
        element: (
          <ChecksContextProvider>
            <Checks />
          </ChecksContextProvider>
        )
      },
      {
        path: "/phonebook/edit-history",
        element: <EditContactHistory />
      },
      {
        path: "/phonebook/redflags",
        element: <RedFlags />
      },
      {
        path: "/phonebook/suspectedMatches",
        element: <SuspectedMatches />
      },
      {
        path: "busdev/order-clients",
        element: <ClientOrders />
      },
      {
        path: "/tasks",
        element: <Navigate to="/outstanding-searches" replace />
      },
      {
        path: "/outstanding-searches",
        element: (
          <OutstandingSearchesContextProvider>
            <OutstandingSearches />
          </OutstandingSearchesContextProvider>
        )
      },
      {
        path: "/searches-not-ordered",
        element: (
          <SearchesNotOrderedProvider>
            <SearchesNotOrdered />
          </SearchesNotOrderedProvider>
        )
      },
      {
        path: "/survey-zoning",
        element: <SurveyZoning />
      },
      {
        path: "/commitments-overdue",
        element: (
          <CommitmentsOverdueContextProvider>
            <CommitmentsOverdue />
          </CommitmentsOverdueContextProvider>
        )
      },
      {
        path: "/dashboard",
        element: <ManagersDashboard />
      },
      {
        path: "/user-dashboard/:user",
        element: <MyDashboard />,
        title: "User Dashboard"
      },
      {
        path: "/recordables",
        element: (
          <OrderInfoProvider>
            <RecordablesPage />
          </OrderInfoProvider>
        )
      },
      {
        path: "/recordables/tasks",
        element: (
          <OrderInfoProvider>
            <TasksPage />
          </OrderInfoProvider>
        )
      },
      {
        path: "/recordables/manage-tasks",
        element: <ManageTasksPage />
      },
      {
        path: "/recordables/rejections",
        element: (
          <Suspense fallback={null}>
            <RecordableRejections />
          </Suspense>
        ),
        title: "Recording Rejections"
      },
      {
        path: "/deposit-refunds/view",
        element: <DepositRefundsPage />
      },
      {
        path: "/deposit-refunds/new",
        element: <NewDepositRefundRequest />,
        title: "Deposit Refunds"
      },
      {
        path: "/corpdocs",
        element: <CorpDocsPage />
      },
      {
        path: "/corpdocs/tasks",
        element: <CorpDocsTasksPage />
      },
      {
        path: "/corpdocs/manage-tasks",
        element: <CorpDocsManageTasksPage />,
        title: "Corpdocs Manage Tasks"
      },
      {
        path: "/policies-not-issued",
        element: (
          <PoliciesNotIssuedContextProvider>
            <PoliciesNotIssued />
          </PoliciesNotIssuedContextProvider>
        )
      },
      {
        path: "/escrow-overdue-orders",
        element: (
          <EscrowOverdueOrdersContextProvider>
            <EscrowOverdueOrders />
          </EscrowOverdueOrdersContextProvider>
        )
      },
      {
        path: "/escrow-ledgers",
        element: (
          <EscrowLedgersContextProvider>
            <EscrowLedgers />
          </EscrowLedgersContextProvider>
        )
      },
      {
        path: "/recording-ledgers",
        element: (
          <RecordingLedgersContextProvider>
            <RecordingLedgers />
          </RecordingLedgersContextProvider>
        )
      },
      { path: "/reporting", element: <ReportingPage />, title: "Reporting" },
      {
        path: "/non-zero-ledger-balances",
        element: (
          <NonZeroBalanceLedgersContextProvider>
            <NonZeroBalanceLedgers />
          </NonZeroBalanceLedgersContextProvider>
        )
      },
      {
        path: "/deposits",
        element: (
          <NonZeroBalanceLedgersContextProvider reportSource="deposits">
            <NonZeroBalanceLedgers pageTitle="Deposits" reportSource="deposits" />
          </NonZeroBalanceLedgersContextProvider>
        )
      },
      {
        path: "/TrustMoneyPieChart",
        element: <TrustMoneyPieChart />
      },
      {
        path: "/non-zero-ledger-balances-old",
        element: <NonZeroLedgerBalanceOld />
      },
      {
        path: "/non-zero-ledger-balances-ny",
        element: <NonZeroLedgerBalance_NY />
      },
      {
        path: "/non-zero-ledger-balances-redirect",
        element: <NonZeroLedgerBalanceRedirect />
      },
      {
        path: "/flips-dashboard",
        element: (
          <FlipsDashboardContextProvider>
            <FlipsDashboard />
          </FlipsDashboardContextProvider>
        )
      },
      {
        path: "/insurance-audit",
        children: [
          {
            index: true,
            element: (
              <OrderInfoProvider>
                <InsuranceAudit />
              </OrderInfoProvider>
            )
          }
        ]
      },
      {
        path: "/loginsuccess",
        element: <PageLoginSucess />,
        showInDirectory: false
      },
      {
        path: "/dashboard2",
        element: <Dashboard />,
        showInDirectory: false
      },
      {
        path: "/internal-user-roles",
        element: <ManagerProtectedRoute element={<InternalUserRoles />} />
      },
      {
        path: "/internal-user-roles/edit/:id",
        element: <ManagerProtectedRoute element={<EditInternalUserRole />} />
      },
      { path: "/unauthorized", element: <Unauthorized />, showInDirectory: false },
      { path: "/blacklistSearch", element: <BlackListNameSearch /> },
      { path: "/newBlacklistSearch", element: <NewBlackListNameSearch /> },
      { path: "/patriotSearch", element: <PatriotNameSearch /> },
      { path: "/qa-review", element: <PendingQaOrders />, title: "Pending QA", showInDirectory: false },
      { path: "/task-management", element: <Navigate to="/task-managementv1/flips" replace /> },
      {
        path: "/task-management/closingchecklist",
        element: (
          <OrderInfoProvider>
            <NewTasks />
          </OrderInfoProvider>
        ),
        title: "Closing Checklist",
        orderNumberQueryParam: "order"
      },
      {
        path: "/task-management/closingchecklist/marked-up-commitment/:orderNumber",
        element: <MarkedUpCommitment />,
        title: "Marked Up Commitment"
      },
      {
        path: "/task-managementv1/:taskType",
        element: (
          <OrderInfoProvider>
            <Tasks />
          </OrderInfoProvider>
        )
      },

      {
        path: "/task-management/:taskType",
        element: (
          <OrderInfoProvider>
            <NewTasks />
          </OrderInfoProvider>
        )
      },
      { path: "/operations/missing-principal", element: <ExecutiveReports /> },
      { path: "/operations/client-interactions", element: <ClientInteractions /> },
      { path: "/commission-tool", element: <CommissionTool /> },
      { path: "/ledger/:orderNumber", element: <TransactionLedger /> },
      { path: "/add-blacklist-name", element: <AddBlacklistName /> },
      { path: "/add-blacklist-property", element: <AddBlacklistProperty /> },
      { path: "/blacklist-migration-review", element: <BlacklistMigrationReview />, title: "Blacklist Migration Review" },
      { path: "/edit-blacklist-name", element: <EditBlacklistName /> },
      { path: "/edit-blacklist-property", element: <EditBlacklistProperty /> },
      {
        path: "/policies-to-send",
        element: (
          <PoliciesToSendProvider>
            <PoliciesToSendDashboard />
          </PoliciesToSendProvider>
        )
      },
      {
        path: "/policy-issues",
        element: (
          <PolicyIssuesProvider>
            <PolicyIssuesDashboard />
          </PolicyIssuesProvider>
        )
      },
      { path: "/view-bulletins", element: <ViewBulletins /> },
      { path: "/newly-added-to-phonebook", element: <ViewContacts /> },
      {
        path: "/client-cultivator-dashboard",
        element: <ManagerProtectedRoute element={<ClientCultivatorDashboard />} />,
        title: "Client Cultivator Dashboard"
      },
      { path: "/dashboard-permissions", element: <ManagerProtectedRoute element={<DashboardPermissionsTable />} /> },
      { path: "/purchase-agreement-extractor", element: <PurchaseAgreementExtractor /> },
      { path: "/custom-document-extractor", element: <CustomDocumentExtractor /> },
      { path: "/multi-order-contact-editor", element: <MultiOrderContactEditor /> },
      { path: "/multi-site-order-info", element: <MultiSiteOrderInfo /> },
      {
        path: "/funding-packages",
        element: (
          <FundingAuthorizationProvider>
            <FundingPackagesRoute />
          </FundingAuthorizationProvider>
        )
      },
      {
        path: "/funding-packages/:orderNumber",
        element: (
          <FundingAuthorizationProvider>
            <FundingPackagesRoute />
          </FundingAuthorizationProvider>
        )
      },
      {
        path: "/edit-funding-package/:compositeId",
        element: (
          <FundingAuthorizationProvider>
            <NewFundingPackageDetails />
          </FundingAuthorizationProvider>
        )
      },
      {
        path: "/review-funding-package/:compositeId",
        element: (
          <FundingAuthorizationProvider>
            <ReviewFundingPackage />
          </FundingAuthorizationProvider>
        )
      },
      { path: "/daily-orders-client-interactions", element: <DailyOrdersClientInteractions /> },
      {
        path: "*",
        element: <Page404 />,
        showInDirectory: false
      }
    ]
  }
];

export default function Router() {
  const { user } = useContext(UserContext);
  const routedElement = useRoutes(routes as RouteObject[]);

  useEffect(() => {
    if (user && user.id && window.location.hostname !== "localhost") {
      const id = user.id.toString();
      const name = user.name || "unknown user";
      const email = user.email || "unknown email";

      setTimeout(() => {
        LogRocket.init("titleq/titleq", {
          mergeIframes: true
        });
        setupLogRocketReact(LogRocket);
        LogRocket.identify(id, { name, email });
      }, 2000);
    }
  }, [user]);

  const location = useLocation();

  useEffect(() => {
    logPageView();
  }, [location]);

  return (
    <>
      <CommandPalette routes={routes} />
      {routedElement}
    </>
  );
}
